import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
// NOTE: Replaced original import with local mock to resolve external path error.
import { useNotification } from "../../../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../../../config/baseUrls";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

// Mock Notification Hook to allow code compilation in isolated environment
// const useNotification = () => ({
//   showSuccess: (title, message) =>
//     console.log(`SUCCESS: ${title} - ${message}`),
//   showError: (title, message) => console.error(`ERROR: ${title} - ${message}`),
// });

const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="p-8 text-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
    <p className="mt-4 text-gray-600">{message}</p>
  </div>
);

// =============================================================================
// MAIN COUPON SELECTION COMPONENT
// =============================================================================

export const CouponSelectionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useNotification();

  const fetchProduct = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/products/${id}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success && data.data) {
        setProduct(data.data);
      } else {
        showError("Error", "Product not found.");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      showError("Error", "Failed to fetch product details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  if (loading) return <LoadingSpinner message="Fetching Product Details..." />;
  if (!product)
    return (
      <div className="p-8 text-center text-red-600">
        Product data unavailable.
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-6 bg-white rounded-xl shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <button
          onClick={() => navigate("/admin/dashboard/product-management")}
          className="px-3 py-2 text-gray-600 hover:text-indigo-600 transition-colors flex items-center"
        >
          <svg
            className="w-5 h-5 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            ></path>
          </svg>
          Back to Products
        </button>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800">
            Coupon Management
          </h1>
          <p className="text-lg text-indigo-600 mt-1">{product.title}</p>
        </div>
        <div></div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold text-gray-700">
          Select Coupon Type to Manage
        </h2>
        <p className="text-gray-500">
          Choose the type of coupon you wish to view, create, or modify.
        </p>
      </div>

      {/* Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
        {/* Card 1: Single-Use (Bulk) Coupons */}
        <div className="border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow bg-blue-50">
          <h3 className="text-2xl font-bold text-blue-800 mb-3">
            Product Coupons (Bulk)
          </h3>
          <p className="text-gray-600 mb-4">
            Manage batches of unique, one-time-use coupon codes (e.g.,
            promotional codes sent to individual users or distributors).
          </p>
          <Link
            to={`/admin/dashboard/product-management/coupons/${id}/single-use`}
            className="block w-full text-center px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
          >
            Manage Single-Use Coupons 🎟️
          </Link>
        </div>

        {/* Card 2: Campaign (Multi-Use) Coupons */}
        <div className="border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow bg-green-50">
          <h3 className="text-2xl font-bold text-green-800 mb-3">
            Campaign Coupons (Multi-Use)
          </h3>
          <p className="text-gray-600 mb-4">
            Manage reusable coupon codes with a specific deadline and a total
            usage limit (e.g., general campaign codes like "FLASH20").
          </p>
          <Link
            to={`/admin/dashboard/product-management/coupons/${id}/campaign`}
            className="block w-full text-center px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-md"
          >
            Manage Campaign Coupons 🚀
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CouponSelectionPage;
